import React, { useEffect, useRef, useState } from 'react';
import TopBar from 'components/TopBar/TopBar';
import Copyright from 'components/Copyright/Copyright';
import Button from 'components/Button/Button';
import QuoteForm from 'components/QuoteForm/QuoteForm';
import routes from 'routes/routes';
import {
	Section,
	SectionInner,
	SectionBody,
	SectionHeader,
	SectionEyebrow,
	SectionTitle,
	SectionSubtitle,
	Grid,
	Item,
	ItemTitle,
	ItemText,
} from 'components/Section/styled';
import {
	Wrapper,
	Intro,
	IntroEyebrow,
	IntroTitle,
	IntroLead,
	QuoteBlock,
	QuoteTitle,
	QuoteText,
	QuoteMeta,
	Closing,
	ClosingTitle,
	ClosingText,
} from './styled';

/**
 * No plan table on the page. The two plans still exist — they arrive inside
 * the quote, already priced for the institution that asked — but publishing
 * them as a table means either printing a rate that is wrong for most readers
 * or printing "contact us" twice, and neither is worth the space.
 */
const STEPS = [
	{
		title: 'Tell us your size',
		text:
			'Three questions: who would use Flass, roughly how many people, and when you would want to start. Nothing technical.',
	},
	{
		title: 'We price both plans',
		text:
			'Every plan costed at your headcount — both are open to you — so you can see what moving up or down actually costs before committing.',
	},
	{
		title: 'Take it to your budget holder',
		text:
			'A written quote with real figures, usually within two working days. No sales call needed to get a number out of us.',
	},
];

/**
 * Included everywhere. Deliberately not split into tiers: withholding teaching
 * or safety features from smaller departments would be a worse product, and a
 * comparison table full of half-empty columns is a worse page.
 */
const INCLUDED = [
	{
		title: 'Course rooms and direct messages',
		text:
			'Rooms for every module, cohort, and society, plus one-to-one and group messages, with an entire intake provisioned in one step.',
	},
	{
		title: 'Office hours with a queue',
		text:
			'Staff open a session, students join a line, and everyone can see where they stand until the slot closes.',
	},
	{
		title: 'Polls, surveys, and events',
		text:
			'Check understanding, collect module feedback, and publish events without moving the conversation to another tool.',
	},
	{
		title: 'End-to-end encryption',
		text:
			'Messages and notification previews are encrypted under per-room keys that are only unwrapped on a signed-in device.',
	},
	{
		title: 'Files, recordings, and documents',
		text:
			'Share slides, PDFs, images, video, and audio, with previews that open in the conversation rather than a download folder.',
	},
	{
		title: 'Anonymous rooms',
		text:
			'Run feedback, wellbeing, and peer-support discussions under consistent pseudonyms instead of real names.',
	},
	{
		title: 'Marketplace and jobs board',
		text:
			'Keep student-to-student trading and part-time roles on a service the university runs and can moderate.',
	},
	{
		title: 'Web, iOS, and Android',
		text:
			'The same product in the browser and as native apps, with per-room notification settings on every device.',
	},
];

const Pricing = (props) => {
	const [showForm, setShowForm] = useState(false);
	const quoteRef = useRef(null);

	useEffect(() => {
		document.title = 'Pricing | Flass';
	});

	const openForm = () => {
		setShowForm(true);
		// The form is long, so bring its start to the top rather than leaving
		// the reader wherever the button happened to be.
		window.requestAnimationFrame(() => {
			if (quoteRef.current) {
				quoteRef.current.scrollIntoView({
					behavior: 'smooth',
					block: 'start',
				});
			}
		});
	};

	return (
		<Wrapper>
			<TopBar />

			<Intro>
				<IntroEyebrow>Pricing</IntroEyebrow>
				<IntroTitle>Priced to your institution.</IntroTitle>
				<IntroLead>
					What changes between plans is mostly size — so instead of a
					table that would be wrong for most readers, tell us roughly
					how big you are and we will send back a quote with both
					plans priced for you.
				</IntroLead>
			</Intro>

			<Section>
				<SectionInner>
					<SectionBody>
						<SectionHeader>
							<SectionEyebrow>How it works</SectionEyebrow>
							<SectionTitle>
								Two plans, priced for you
							</SectionTitle>
							<SectionSubtitle>
								Plans mostly differ by how much room each
								person gets and how far back history goes —
								plus voice and video calling, which is part of
								the higher plan. Your size sets the total, not
								which plan you are allowed — both plans are
								open to every institution.
							</SectionSubtitle>
						</SectionHeader>

						<Grid $three>
							{STEPS.map((step) => (
								<Item key={step.title}>
									<ItemTitle>{step.title}</ItemTitle>
									<ItemText>{step.text}</ItemText>
								</Item>
							))}
						</Grid>

						<QuoteBlock ref={quoteRef}>
							{showForm ? (
								<>
									<QuoteTitle>Get a quote</QuoteTitle>
									<QuoteText>
										Seven questions, five of them required.
										It takes about a minute.
									</QuoteText>
									<QuoteForm />
								</>
							) : (
								<>
									<QuoteTitle>
										Get a price for your institution
									</QuoteTitle>
									<QuoteText>
										A minute of questions about who you are
										and how big your deployment would be. We
										will come back with both plans
										priced, usually within two working days.
									</QuoteText>
									<Button onClick={openForm} size='medium'>
										Get a quote
									</Button>
									<QuoteMeta>
										No sales call required to get a number.
										Prices exclude VAT.
									</QuoteMeta>
								</>
							)}
						</QuoteBlock>
					</SectionBody>
				</SectionInner>
			</Section>

			<Section $alt>
				<SectionInner>
					<SectionBody>
						<SectionHeader>
							<SectionEyebrow>Included everywhere</SectionEyebrow>
							<SectionTitle>
								Every deployment is the full product
							</SectionTitle>
							<SectionSubtitle>
								Teaching tools and student safety features are
								not held back for larger institutions. A single
								department gets the same Flass a whole
								university does — the one exception is voice
								and video calling, which is part of the higher
								plan.
							</SectionSubtitle>
						</SectionHeader>
						<Grid>
							{INCLUDED.map((feature) => (
								<Item key={feature.title}>
									<ItemTitle>{feature.title}</ItemTitle>
									<ItemText>{feature.text}</ItemText>
								</Item>
							))}
						</Grid>
					</SectionBody>
				</SectionInner>
			</Section>

			<Closing>
				<ClosingTitle>Still working out what you need?</ClosingTitle>
				<ClosingText>
					If you are not sure yet how many people to count, put your
					best guess in. Working that out with institutions is most of
					what we do before a quote goes back.
				</ClosingText>
				<Button
					onClick={() => props.history.push(routes.WHY_FLASS)}
					size='medium'
					type='secondary'>
					See what Flass does
				</Button>
			</Closing>

			<Copyright />
		</Wrapper>
	);
};

export default Pricing;
